/**
 * Values both credited-person surfaces share.
 *
 * The one product setting here is `CREDIT_ROLES`, and it is **read first and
 * seeded second** — never invented and never shipped empty.
 */

export const PEOPLE_ROUTES = {
  register: '/people',
  person: (slug: string) => `/people/${encodeURIComponent(slug)}`,
} as const;

/**
 * `credit_roles`, as the feature catalog's «Configuration, not features» table
 * declares it: **director, producer, writer, actor, composer, cinematographer**
 * — six and no seventh. `backend-content` seeds the identical list into
 * `CREDIT_ROLES` (`content__configurations`).
 *
 * This is a **fallback, not the source**. `GET /api/v1/admin/configurations` on
 * `backend-content` serves the live value, and the surfaces read it. But that
 * route is gated on the configuration permission — the `admin` role — while
 * `/people/:slug` is an **operator** route, so an operator opening the credit
 * dialog meets a `403` and would otherwise be handed an empty `<select>`.
 * Shipping `[]` there is not the safe option: it is an invented value the
 * product never chose, and it silently deletes the one control the dialog
 * exists for.
 *
 * Each member cites the row it came from. When the read succeeds it wins; this
 * list is what the operator gets when it does not.
 */
export const CREDIT_ROLES_FALLBACK: readonly string[] = [
  'director', // catalog «Configuration, not features» → `credit_roles`
  'producer', // catalog «Configuration, not features» → `credit_roles`
  'writer', // catalog «Configuration, not features» → `credit_roles`
  'actor', // catalog «Configuration, not features» → `credit_roles`
  'composer', // catalog «Configuration, not features» → `credit_roles`
  'cinematographer', // catalog «Configuration, not features» → `credit_roles`
];

/** The configuration key the role vocabulary lives under. */
export const CREDIT_ROLES_KEY = 'CREDIT_ROLES';

/**
 * The service's own ceiling on a page (`Query(ge=1, le=100)`), asked for
 * explicitly on the two reads whose completeness the surface states out loud —
 * a person's credit list and the register's role and works columns.
 *
 * It is the callee's bound quoted back, not a page size this console chose: a
 * number invented here would put a server-owned limit behind a rebuild.
 */
export const MAX_PAGE_SIZE = 100;

/**
 * The artwork subject a credited person is, and the one purpose it carries.
 *
 * `assets` owns both as configuration — `ARTWORK_SUBJECT_TYPES` and
 * `ARTWORK_PURPOSES` — and this pair is the **address** those rows are read at,
 * not a copy of their contents. The ratio, the size cap, the accepted formats
 * and the minimum geometry all still come back from `/uploadPolicy`.
 */
export const PORTRAIT_SUBJECT_TYPE = 'CREDIT_PROFILE';
export const PORTRAIT_PURPOSE = 'portrait';

/** Megabytes are what the policy publishes; bytes are what a `File.size` is. */
export const BYTES_PER_MB = 1_048_576;
